import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Search, Rss, Linkedin } from 'lucide-react';
import PageHead from '../components/PageHead';
import { articles as externalArticles } from '../data/portfolio';
import { articleContent } from '../data/articles-content';

// Merge local (on-portfolio) and external (LinkedIn etc.) articles into
// one chronological feed. Local articles win in styling — they get a
// prominent "Read on site" affordance instead of an external arrow.

const parseDate = (s) => {
  if (!s) return 0;
  // Accept both 'YYYY-MM-DD' and 'DD-MM-YYYY'
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s).getTime();
  const [d, m, y] = s.split('-');
  return new Date(`${y}-${m}-${d}`).getTime();
};

const Writing = () => {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'onsite' | 'external'

  const feed = useMemo(() => {
    const onsite = articleContent.map((a) => ({
      kind: 'onsite',
      slug: a.slug,
      date: a.date,
      title: a.title,
      sub: a.subtitle,
      highlight: a.body.replace(/[#>*_`\[\]()-]/g, '').replace(/\s+/g, ' ').trim().slice(0, 200) + '…',
      tags: a.tags,
      readingTime: a.readingTime
    }));

    // Build sets for deduplication: onsite slugs and normalized titles
    const onsiteSlugs = new Set(articleContent.map((a) => (a.slug || '').toLowerCase()));
    const normalize = (s = '') => s.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
    const onsiteTitles = new Set(articleContent.map((a) => normalize(a.title)));

    const external = externalArticles
      .filter((a) => {
        const link = (a.link || '');
        const m = link.match(/#\/writing\/([^/?#]+)/);
        // Exclude external entries that point to onsite slug
        if (m?.[1] && onsiteSlugs.has(m[1].toLowerCase())) return false;
        // Exclude if external title matches an onsite title (normalized)
        if (onsiteTitles.has(normalize(a.title))) return false;
        return true;
      })
      .map((a) => ({
        kind: 'external',
        date: a.date,
        title: a.title,
        sub: a.sub,
        highlight: a.highlight,
        link: a.link
      }));

    return [...onsite, ...external].sort((a, b) => parseDate(b.date) - parseDate(a.date));
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return feed.filter((a) => {
      if (filter !== 'all' && a.kind !== filter) return false;
      if (!term) return true;
      return [a.title, a.sub, a.highlight, a.date, (a.tags || []).join(' ')]
        .some((f) => (f || '').toLowerCase().includes(term));
    });
  }, [q, filter, feed]);

  const onsiteCount = feed.filter((a) => a.kind === 'onsite').length;
  const externalCount = feed.length - onsiteCount;

  return (
    <div className="max-w-3xl mx-auto px-6 pb-8">
      <PageHead eyebrow="Thought Leadership" title="Published articles & insights" />

      <p className="text-[16px] text-ink-soft mt-2 mb-8 max-w-2xl leading-relaxed">
        Field notes on SRE, disaster recovery, chaos engineering, and applied Generative AI — written for engineering
        leaders shaping resilient platforms.
      </p>

      <div className="relative mb-4">
        <Search
          size={16}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-mute pointer-events-none"
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search articles by topic — SRE, AI, DR…"
          className="w-full h-11 pl-11 pr-4 rounded-lg bg-card/40 border border-border text-[16px] focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/25 transition-colors"
        />
        {q && (
          <button
            onClick={() => setQ('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] mono uppercase tracking-widest text-ink-mute hover:text-foreground px-2 py-1"
          >
            clear
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {[
          { k: 'all', l: `All (${feed.length})` },
          { k: 'onsite', l: `On this site (${onsiteCount})`, icon: Rss },
          { k: 'external', l: `LinkedIn (${externalCount})`, icon: Linkedin }
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            className={`inline-flex items-center gap-2 h-9 px-4 rounded-lg text-[13px] transition-all ${
              filter === t.k
                ? 'bg-brand text-black border border-brand'
                : 'border border-border text-ink-soft hover:bg-brand/10 hover:text-foreground hover:border-brand/60 hover:shadow-md hover:-translate-y-0.5'
            }`}
          >
            {t.icon && <t.icon size={14} />} {t.l}
          </button>
        ))}
      </div>

      <div className="mono uppercase tracking-widest text-[10.5px] text-ink-mute mb-4">
        {filtered.length} {filtered.length === 1 ? 'article' : 'articles'}
      </div>

      <div className="space-y-4">
        {filtered.map((a, i) => {
          const isOnsite = a.kind === 'onsite';
          const Wrapper = isOnsite ? Link : 'a';
          const wrapperProps = isOnsite
            ? { to: `/writing/${a.slug}` }
            : { href: a.link, target: '_blank', rel: 'noreferrer' };

          return (
            <Wrapper
              key={i}
              {...wrapperProps}
              className="group block rounded-xl border border-border p-6 bg-card/40 hover:bg-card/70 hover:border-brand/60 hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span
                      className={`mono uppercase tracking-widest text-[10px] px-2 py-0.5 rounded-lg border ${
                        isOnsite
                          ? 'border-brand/50 text-brand bg-brand/5'
                          : 'border-border text-ink-mute'
                      }`}
                    >
                      {isOnsite ? 'On site' : 'LinkedIn'}
                    </span>
                    <span className="mono uppercase tracking-widest text-[10.5px] text-ink-mute">{a.date}</span>
                    {isOnsite && a.readingTime && (
                      <span className="mono uppercase tracking-widest text-[10.5px] text-ink-mute">
                        · {a.readingTime} min read
                      </span>
                    )}
                  </div>
                  <h3 className="text-[18px] md:text-[19px] font-medium tracking-tight leading-snug group-hover:text-brand transition-colors">
                    {a.title}
                  </h3>
                  {a.sub && <div className="text-[13.5px] text-ink-soft mt-1">{a.sub}</div>}
                  <p className="mt-4 text-[13.5px] leading-relaxed text-ink-mute line-clamp-3">{a.highlight}</p>
                  {isOnsite && (
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] mono uppercase tracking-widest text-brand">
                      Read article <ArrowRight size={13} />
                    </div>
                  )}
                </div>
                {!isOnsite && (
                  <ArrowUpRight
                    size={18}
                    className="shrink-0 text-ink-mute group-hover:text-brand group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
                  />
                )}
              </div>
            </Wrapper>
          );
        })}

        {filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-ink-mute text-[13.5px]">
            No articles match “{q}”. Try SRE, chaos, DR, or Gen-AI.
          </div>
        )}
      </div>
    </div>
  );
};

export default Writing;
